// Steps/Step3Assignment.tsx
import { useMemo, useState } from 'react';
import {
  Avatar, Badge, Card, Center, Group, ScrollArea, Stack, Text,
} from '@mantine/core';
import { IconSearch, IconUserCheck } from '@tabler/icons-react';
import { Select as CCMSSelect, TextInput } from '@/components';
import { resolveAvatarSrc, getAvatarInitials } from '@/utils/avatar';
import type { WizardState } from '../types';

/** Display name for a leader row, tolerating the several shapes the list model can arrive in. */
function leaderName(l: any): string {
  return [l.firstName, l.surname].filter(Boolean).join(' ').trim()
    || l.fullName?.trim()
    || l.displayName?.trim()
    || l.userId;
}

export default function Step3Assignment(props: WizardState) {
  const {
    allLocations, allZones, allAreas, allLeaders,
    locationID, zoneID, areaID, leaderUserId,
    handleLocationChange, handleZoneChange, handleAreaChange, setLeaderUserId,
  } = props;

  const [leaderSearch, setLeaderSearch] = useState('');

  const selectedLeader = useMemo(
    () => (allLeaders as any[]).find((l) => l.userId === leaderUserId) ?? null,
    [allLeaders, leaderUserId],
  );

  /**
   * Leaders are already loaded by the wizard, so this filters in memory rather than hitting the
   * search endpoint. Matches name, email, phone and leader type, which is what someone actually
   * knows when looking for the right person.
   */
  const filteredLeaders = useMemo(() => {
    const term = leaderSearch.trim().toLowerCase();
    const list = allLeaders as any[];
    if (!term) return list;
    return list.filter((l) =>
      [leaderName(l), l.email, l.phone, l.typeName]
        .some((field) => String(field ?? '').toLowerCase().includes(term)),
    );
  }, [allLeaders, leaderSearch]);

  return (
    <Stack gap="md">
      <CCMSSelect
        label="Location"
        placeholder="Select a location"
        data={allLocations.map((l) => ({
          value: String(l.locationID),
          label: l.name ?? l.locationName ?? `Location ${l.locationID}`,
        }))}
        value={locationID ? String(locationID) : null}
        onChange={handleLocationChange}
        searchable
      />
      <CCMSSelect
        label="Zone"
        placeholder="Select a zone"
        data={allZones.map((z) => ({ value: String(z.zoneID), label: z.name ?? `Zone ${z.zoneID}` }))}
        value={zoneID ? String(zoneID) : null}
        onChange={handleZoneChange}
        disabled={!locationID}
        searchable
      />
      <CCMSSelect
        label="Area"
        placeholder="Select an area"
        data={allAreas.map((a) => ({ value: String(a.areaID), label: a.name ?? `Area ${a.areaID}` }))}
        value={areaID ? String(areaID) : null}
        onChange={handleAreaChange}
        disabled={!zoneID}
        searchable
      />

      {/* ── Leader: search + pick a card, rather than a bare dropdown ──────── */}
      <Stack gap="xs">
        <Text size="sm" fw={500}>Leader</Text>

        {!areaID ? (
          <Text size="xs" c="dimmed">Choose an area first.</Text>
        ) : (
          <>
            <TextInput
              placeholder="Search leaders by name, email, phone or type…"
              leftSection={<IconSearch size={16} />}
              value={leaderSearch}
              onChange={(e) => setLeaderSearch(e.currentTarget.value)}
            />

            {selectedLeader && (
              <Card withBorder radius="md" padding="sm" style={{ borderColor: 'var(--mantine-primary-color-filled)' }}>
                <Group gap="sm" wrap="nowrap">
                  <IconUserCheck size={18} color="var(--mantine-primary-color-filled)" />
                  <Text size="sm" fw={600} style={{ flex: 1 }}>{leaderName(selectedLeader)}</Text>
                  <Badge variant="light">Selected</Badge>
                </Group>
              </Card>
            )}

            {filteredLeaders.length === 0 ? (
              <Center py="sm">
                <Text size="sm" c="dimmed">
                  {leaderSearch.trim()
                    ? `No leaders match "${leaderSearch.trim()}".`
                    : 'No leaders available.'}
                </Text>
              </Center>
            ) : (
              <ScrollArea.Autosize mah={260}>
                <Stack gap="xs">
                  {filteredLeaders.map((l: any) => {
                    const isSelected = l.userId === leaderUserId;
                    const name = leaderName(l);
                    const avatarSrc = resolveAvatarSrc(l.avatar);
                    return (
                      // Whole card selects, matching how counselees are picked on the previous step.
                      <Card
                        key={l.userId}
                        withBorder
                        radius="md"
                        padding="sm"
                        onClick={() => setLeaderUserId(l.userId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setLeaderUserId(l.userId);
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          borderColor: isSelected ? 'var(--mantine-primary-color-filled)' : undefined,
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap">
                            <Avatar src={avatarSrc} alt={name} radius="xl" size={40}>
                              {getAvatarInitials(l.firstName, l.surname)}
                            </Avatar>
                            <Stack gap={0}>
                              <Text size="sm" fw={600}>{name}</Text>
                              {(l.email || l.phone) && (
                                <Text size="xs" c="dimmed">{l.email || l.phone}</Text>
                              )}
                            </Stack>
                          </Group>

                          <Group gap={6} wrap="nowrap">
                            {l.typeName && (
                              <Badge size="sm" variant="light">{l.typeName}</Badge>
                            )}
                            {typeof l.activeCaseCount === 'number' && (
                              <Badge size="sm" variant="outline" color="gray">
                                {l.activeCaseCount} active
                              </Badge>
                            )}
                            {isSelected && <Badge size="sm" color="blue">Selected</Badge>}
                          </Group>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}
